import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import googleSheetsService, { initializeGoogleSheets } from '../utils/googleSheetsService';
import { getSubjectName, groupDataByLesson } from '../utils/dataProcessor';

const SummaryNew = () => {
  const [loading, setLoading] = useState(true);
  const [sheetsConnected, setSheetsConnected] = useState(false);
  const [selectedModule, setSelectedModule] = useState('LM2001020201_요구사항확인');
  const [summaryData, setSummaryData] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // 기존 모듈 목록 (기존 Summary.js와 동일)
  const modules = [
    { id: 'LM2001020201_요구사항확인', name: '요구사항 확인' },
    { id: 'LM2001020205_데이터입출력구현', name: '데이터입출력구현' },
    { id: 'LM2001020206_통합구현', name: '통합구현' },
    { id: 'LM2001020208_정보시스템이행', name: '정보시스템이행' },
    { id: 'LM2001020209_제품소프트웨어패키징', name: '제품소프트웨어패키징' },
    { id: 'LM2001020211_서버프로그램구현', name: '서버프로그램구현' },
    { id: 'LM2001020212_인터페이스구현', name: '인터페이스구현' },
    { id: 'LM2001020214_애플리케이션배포', name: '애플리케이션배포' },
    { id: 'LM2001020217_애플리케이션리팩토링', name: '애플리케이션리팩토링' },
    { id: 'LM2001020218_인터페이스설계', name: '인터페이스설계' },
    { id: 'LM2001020219_애플리케이션요구사항분석', name: '애플리케이션요구사항분석' },
    { id: 'LM2001020220_기능모델링', name: '기능모델링' },
    { id: 'LM2001020221_애플리케이션설계', name: '애플리케이션설계' },
    { id: 'LM2001020222_정적모델설계', name: '정적모델설계' },
    { id: 'LM2001020223_동적모델설계', name: '동적모델설계' },
    { id: 'LM2001020224_화면설계', name: '화면설계' },
    { id: 'LM2001020225_화면구현', name: '화면구현' },
    { id: 'LM2001020226_애플리케이션테스트관리', name: '애플리케이션테스트관리' },
    { id: 'LM2001020227_애플리케이션테스트수행', name: '애플리케이션테스트수행' },
    { id: 'LM2001020228_소프트웨어공학활용', name: '소프트웨어공학활용' },
    { id: 'LM2001020229_소프트웨어개발방법론활용', name: '소프트웨어개발방법론활용' },
    { id: 'LM2001020230_프로그래밍 언어 응용', name: '프로그래밍 언어 응용' },
    { id: 'LM2001020231_프로그래밍 언어 활용', name: '프로그래밍 언어 활용' },
    { id: 'LM2001020232_응용SW 기초 기술 활용', name: '응용SW 기초 기술 활용' },
    { id: 'LM2001020233_개발자 환경 구축', name: '개발자 환경 구축' },
    { id: 'LM2001020234_개발 환경 운영 지원', name: '개발 환경 운영 지원' }
  ];

  useEffect(() => {
    initializeConnection();
  }, []);

  useEffect(() => {
    if (sheetsConnected) {
      loadModuleData();
    }
  }, [selectedModule, sheetsConnected]);

  // Google Sheets 연결 초기화
  const initializeConnection = async () => {
    try {
      const connected = await initializeGoogleSheets();
      setSheetsConnected(connected);
      
      if (!connected) {
        // 연결 실패시 더미 데이터 사용
        generateDummyData();
      }
    } catch (error) {
      console.error('Google Sheets 연결 실패:', error);
      setSheetsConnected(false);
      generateDummyData();
    } finally {
      setLoading(false);
    }
  };

  // 선택된 모듈 데이터 로드 (기존 Summary.js 로직)
  const loadModuleData = async () => {
    try {
      setLoading(true);
      
      if (sheetsConnected) {
        // Google Sheets에서 데이터 로드 시도
        try {
          const data = await googleSheetsService.getSubjectData(selectedModule);
          if (data && data.length > 0) {
            const groupedData = groupDataByLesson(data);
            setSummaryData(groupedData);
            return;
          }
        } catch (error) {

        }
      }
      
      // Google Sheets 실패시 또는 연결 안된 경우 기존 JSON 데이터 사용
      await loadChunksData();
      
    } catch (error) {
      console.error('모든 데이터 로드 실패:', error);
      generateFallbackData();
    } finally {
      setLoading(false);
    }
  };

  // 기존 JSONL 데이터 로드 (Summary.js와 동일한 로직)
  const loadChunksData = async () => {
    try {
      // JSONL 파일 로드 (public 폴더에서 접근)
      const response = await fetch('/all_chunks.jsonl');
      const text = await response.text();
      
      // JSONL 파싱 (각 줄이 JSON)
      const chunks = text
        .split('\n')
        .filter(line => line.trim())
        .map(line => JSON.parse(line));
      
      // 선택된 모듈의 데이터만 필터링
      const moduleChunks = chunks.filter(chunk => chunk.doc_id === selectedModule);
      
      generateSummary(moduleChunks);
    } catch (error) {
      console.error('청크 데이터 로드 실패:', error);
      // 에러 시 더미 데이터로 테스트
      generateDummyData();
    }
  };

  // 기존 Summary.js의 generateSummary 로직을 완전히 복사
  const generateSummary = (chunks) => {
    const summary = {};
    
    // 학습모듈별로 그룹화
    const groupedByLesson = chunks.reduce((acc, chunk) => {
      const lesson = chunk.lesson_num;
      if (!acc[lesson]) {
        acc[lesson] = {
          title: chunk.lesson_title,
          topics: {},
          totalChunks: 0,
          totalLength: 0
        };
      }
      
      acc[lesson].totalChunks++;
      acc[lesson].totalLength += chunk.length;
      
      // 토픽별 그룹화
      const topicId = chunk.topic_id;
      if (!acc[lesson].topics[topicId]) {
        acc[lesson].topics[topicId] = {
          title: chunk.topic_title,
          chunks: [],
          keyTerms: new Set(),
          contentTypes: []
        };
      }
      
      acc[lesson].topics[topicId].chunks.push(chunk);
      
      // 키워드 추출 (간단한 버전)
      const keywords = extractKeywords(chunk.text);
      keywords.forEach(keyword => acc[lesson].topics[topicId].keyTerms.add(keyword));
      
      // 내용 유형 추가
      if (chunk.annotations && chunk.annotations.type) {
        acc[lesson].topics[topicId].contentTypes.push(...chunk.annotations.type);
      }
      
      return acc;
    }, {});
    
    // Set을 Array로 변환
    Object.keys(groupedByLesson).forEach(lesson => {
      Object.keys(groupedByLesson[lesson].topics).forEach(topicId => {
        groupedByLesson[lesson].topics[topicId].keyTerms = 
          Array.from(groupedByLesson[lesson].topics[topicId].keyTerms);
      });
    });
    
    setSummaryData(groupedByLesson);
  };

  // 키워드 추출 (기존 로직)
  const extractKeywords = (text) => {
    const techTerms = text.match(/[가-힣]{2,}(?:시스템|모델|방법|기법|분석|설계|구현|테스트|관리|요구사항|인터페이스|아키텍처|소프트웨어|하드웨어|네트워크|데이터베이스)/g) || [];
    const definitions = text.match(/[가-힣]{3,}(?:이란|란|의 정의|개념)/g) || [];
    
    return [...new Set([...techTerms, ...definitions])].slice(0, 5);
  };

  // 요약 텍스트 생성
  const generateSummaryText = (chunks) => {
    if (chunks.length === 0) return '';
    
    // 첫 번째 청크의 앞부분을 요약으로 사용
    const firstChunk = chunks[0];
    const text = firstChunk.text;
    
    if (text.length <= 100) return text;
    
    const firstSentence = text.split('.')[0];
    if (firstSentence.length <= 100) {
      return firstSentence + '.';
    }
    
    return text.substring(0, 97) + '...';
  };

  // 에러 시 더미 데이터 생성 (기존 Summary.js와 동일)
  const generateDummyData = () => {
    const dummyData = {
      "1": {
        title: "현행 시스템 분석하기",
        topics: {
          "1-1": {
            title: "현행 시스템 파악",
            chunks: [{ text: "현행 시스템 파악은 개발하고자 하는 시스템의 이해를 위해 필요한 과정입니다.", length: 50 }],
            keyTerms: ["현행 시스템", "시스템 분석", "요구사항"],
            contentTypes: ["정의", "절차"]
          }
        },
        totalChunks: 1,
        totalLength: 50
      },
      "2": {
        title: "요구사항 정의",
        topics: {
          "2-1": {
            title: "요구사항 정의",
            chunks: [{ text: "요구사항 정의는 시스템이 수행해야 할 기능과 제약사항을 명시하는 과정입니다.", length: 45 }],
            keyTerms: ["요구사항", "기능요구사항", "비기능요구사항"],
            contentTypes: ["정의"]
          }
        },
        totalChunks: 1,
        totalLength: 45
      },
      "3": {
        title: "분석모델 확인하기",
        topics: {
          "3-1": {
            title: "분석모델 검증",
            chunks: [{ text: "분석모델 검증은 작성된 분석모델의 정확성과 완전성을 확인하는 과정입니다.", length: 42 }],
            keyTerms: ["분석모델", "검증", "타당성"],
            contentTypes: ["절차"]
          }
        },
        totalChunks: 1,
        totalLength: 42
      }
    };
    setSummaryData(dummyData);
  };

  const generateFallbackData = () => {
    generateDummyData();
  };

  const getSelectedModuleInfo = () => {
    return modules.find(module => module.id === selectedModule) || modules[0];
  };

  const selectedModuleInfo = getSelectedModuleInfo();
  const lessonKeys = Object.keys(summaryData).sort();

  // 토픽 편집
  const handleEditTopic = (lessonNum, topicId, topicData) => {
    setEditingItem({
      lessonNum: lessonNum,
      topicId: topicId,
      data: {
        title: topicData.title,
        keyTerms: topicData.keyTerms.join(', '),
        contentTypes: topicData.contentTypes.join(', '),
        summary: topicData.chunks.map(c => c.text).join('\n\n').substring(0, 200) + '...',
        details: topicData.chunks.map(c => c.text).join('\n\n')
      }
    });
    setEditMode(true);
  };

  // 편집 저장
  const handleSave = async () => {
    try {
      if (!editingItem) return;

      // 로컬 상태 업데이트
      const updatedData = { ...summaryData };
      const lesson = updatedData[editingItem.lessonNum];
      const topic = lesson.topics[editingItem.topicId];
      
      // 토픽 데이터 업데이트
      topic.title = editingItem.data.title;
      topic.keyTerms = editingItem.data.keyTerms.split(',').map(k => k.trim()).filter(k => k);
      topic.contentTypes = editingItem.data.contentTypes.split(',').map(c => c.trim()).filter(c => c);
      
      setSummaryData(updatedData);

      // Google Sheets 업데이트 (연결된 경우)
      if (sheetsConnected) {
        try {
          const sheetTitle = `${selectedModule}_데이터`;
          await googleSheetsService.updateData(sheetTitle, 0, {
            '토픽명': editingItem.data.title,
            '키워드': editingItem.data.keyTerms,
            '내용유형': editingItem.data.contentTypes,
            '상세설명': editingItem.data.details
          });
        } catch (error) {

        }
      }

      setEditMode(false);
      setEditingItem(null);
      
    } catch (error) {
      console.error('저장 실패:', error);
      alert('저장에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 편집 취소
  const handleCancel = () => {
    setEditMode(false);
    setEditingItem(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {sheetsConnected ? '데이터를 불러오는 중...' : 'Google Sheets에 연결하는 중...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">📚 학습 요약정리</h1>
            {sheetsConnected && (
              <div className="flex items-center text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm">Google Sheets 연결됨</span>
              </div>
            )}
          </div>
          <p className="text-gray-600 mb-6">
            NCS 학습모듈의 핵심 내용을 정리하고 편집할 수 있습니다.
            {!sheetsConnected && ' (오프라인 모드)'}
          </p>
          
          {/* 컨트롤 패널 */}
          <div className="bg-white rounded-lg p-6 shadow-sm mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 학습모듈 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  학습모듈 선택
                </label>
                <select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  className="w-full max-w-md px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  {modules.map(module => (
                    <option key={module.id} value={module.id}>
                      {module.name}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedModuleInfo.name}
                </p>
              </div>

              {/* 검색 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  검색
                </label>
                <input
                  type="text"
                  placeholder="제목, 키워드로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* 액션 버튼 */}
              <div className="flex items-end">
                <button
                  onClick={loadModuleData}
                  disabled={!sheetsConnected}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  🔄 새로고침
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 선택된 모듈의 학습 목차 */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            📋 {selectedModuleInfo.name} - 학습 목차
          </h2>
          
          {lessonKeys.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {lessonKeys.map((lessonNum) => {
                const lessonData = summaryData[lessonNum];
                return (
                  <div key={lessonNum} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105">
                    {/* 학습 헤더 */}
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
                      <h3 className="text-lg font-bold mb-2">
                        학습 {lessonNum}. {lessonData.title}
                      </h3>
                      <div className="flex gap-3 text-blue-100 text-sm">
                        <span>📄 {lessonData.totalChunks}개 섹션</span>
                        <span>🎯 {Object.keys(lessonData.topics).length}개 토픽</span>
                      </div>
                      <div className="mt-2 text-blue-100 text-sm">
                        📖 편집 가능한 콘텐츠
                      </div>
                    </div>

                    {/* 토픽 목록 */}
                    <div className="p-4">
                      <div className="space-y-3">
                        {Object.entries(lessonData.topics).map(([topicId, topicData]) => (
                          <div key={topicId} className="border-l-4 border-blue-200 pl-3 py-2 relative group">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900 mb-1">
                                  {topicId}. {topicData.title}
                                </h4>
                                <p className="text-sm text-gray-600 mb-2">
                                  📑 {topicData.chunks.length}개 섹션
                                </p>
                                
                                {/* 주요 키워드 (간단히) */}
                                {topicData.keyTerms.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {topicData.keyTerms.slice(0, 3).map((term, index) => (
                                      <span 
                                        key={index}
                                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                                      >
                                        {term}
                                      </span>
                                    ))}
                                    {topicData.keyTerms.length > 3 && (
                                      <span className="text-xs text-gray-500">
                                        +{topicData.keyTerms.length - 3}개
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* 편집 버튼 */}
                              <button
                                onClick={() => handleEditTopic(lessonNum, topicId, topicData)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                              >
                                ✏️ 편집
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📚</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">학습 내용을 불러오는 중...</h3>
              <p className="text-gray-600">잠시만 기다려주세요.</p>
            </div>
          )}
        </div>

        {/* 모듈 통계 */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">📊 {selectedModuleInfo.name} 통계</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{lessonKeys.length}</div>
              <div className="text-sm text-gray-600">학습 단위</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {Object.values(summaryData).reduce((sum, lesson) => sum + Object.keys(lesson.topics).length, 0)}
              </div>
              <div className="text-sm text-gray-600">총 토픽</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">
                {Object.values(summaryData).reduce((sum, lesson) => sum + lesson.totalChunks, 0)}
              </div>
              <div className="text-sm text-gray-600">총 섹션</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">
                {Math.round(Object.values(summaryData).reduce((sum, lesson) => sum + lesson.totalLength, 0) / 1000)}K
              </div>
              <div className="text-sm text-gray-600">총 글자 수</div>
            </div>
          </div>
        </div>

        {/* 편집 모달 */}
        {editMode && editingItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-lg font-bold mb-4">내용 편집</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      토픽 제목
                    </label>
                    <input
                      type="text"
                      value={editingItem.data.title}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        data: { ...editingItem.data, title: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      내용유형 (쉼표로 구분)
                    </label>
                    <input
                      type="text"
                      value={editingItem.data.contentTypes}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        data: { ...editingItem.data, contentTypes: e.target.value }
                      })}
                      placeholder="정의, 절차, 예시"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      키워드 (쉼표로 구분)
                    </label>
                    <input
                      type="text"
                      value={editingItem.data.keyTerms}
                      onChange={(e) => setEditingItem({
                        ...editingItem,
                        data: { ...editingItem.data, keyTerms: e.target.value }
                      })}
                      placeholder="키워드1, 키워드2, 키워드3"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      요약 (읽기 전용)
                    </label>
                    <textarea
                      value={editingItem.data.summary}
                      readOnly
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      상세 내용 (읽기 전용)
                    </label>
                    <textarea
                      value={editingItem.data.details}
                      readOnly
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-600"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    저장
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryNew;